import _ from 'lodash'
import { unops, binops, methods, isLogop } from './operators.js'
import Parser from './parser.js'
import Serializer from './serializer.js'
import History from './history.js'

export default class Evaluator {
  constructor (enableCache = false) {
    this.parser = new Parser(enableCache)
    this.serializer = new Serializer()
  }

  async evaluate (formula, data) {
    this.unresolved = []
    this.history = new History()

    const resolvedFormula = await this.resolveFormula(formula, data)

    return {
      ...this.history.buildEntry(
        resolvedFormula,
        await this.evaluateNode(
          this.parser.parse(resolvedFormula),
          data
        )
      ),
      ..._.chain({
        unresolved: _.cloneDeep(this.unresolved)
      }).pickBy().omitBy(_.isEmpty).value()
    }
  }

  getMemberKey (node) {
    const k = node.object.type === 'MemberExpression' ? this.getMemberKey(node.object) : node.object.name
    return [..._.castArray(k), node.computed ? node.property.value : node.property.name]
  }

  getValue (key, data) {
    const k = _.castArray(key)

    if (_.has(data, k)) {
      return _.get(data, k)
    }
  }

  addUnresolved (key) {
    this.unresolved = _.chain(this.unresolved).concat(this.serializer.toDotKey(key)).uniq().value()
    return undefined
  }

  getValueOrAddUnresolved (key, data) {
    return this.getValue(key, data) ?? this.addUnresolved(key)
  }

  getValueAndReturnLiteral (key, data) {
    return {
      type: 'Literal',
      value: this.getValue(key, data)
    }
  }

  async resolveFormula (formula, data) {
    return this.serializer.serializeNode(await this.#handleFormulaNode(this.parser.parse(this.serializer.serialize(formula)), data))
  }

  async #handleFormulaNode (node, data, evaluate = false) {
    switch (node?.type) {
      case 'Identifier':
        if (evaluate) {
          node = this.getValueAndReturnLiteral(node.name, data)
        }

        break
      case 'Task':
        if (node.taskArg) {
          node.taskArg = await this.#handleFormulaNode(node.taskArg, data, true)
        }

        break
      case 'Annotation':
        node.expression = await this.#handleFormulaNode(node.expression, data, evaluate)
        break
      case 'Property':
        node.value = await this.#handleFormulaNode(node.value, data, evaluate)
        break
      case 'Compound':
        node.body = await Promise.all(_.map(node.body, n => this.#handleFormulaNode(n, data, evaluate)))
        break
      case 'SequenceExpression':
        node.expressions = await Promise.all(_.map(node.expressions, n => this.#handleFormulaNode(n, data, evaluate)))
        break
      case 'MemberExpression':
        if (evaluate) {
          node = this.getValueAndReturnLiteral(this.getMemberKey(node), data)
        } else {
          node.object = await this.#handleFormulaNode(node.object, data, evaluate)
          node.property = await this.#handleFormulaNode(node.property, data, evaluate)
        }

        break
      case 'UnaryExpression':
        node.argument = await this.#handleFormulaNode(node.argument, data, evaluate)
        break
      case 'LogicalExpression':
      case 'BinaryExpression':
        node.left = await this.#handleFormulaNode(node.left, data, evaluate)
        node.right = await this.#handleFormulaNode(node.right, data, evaluate)
        break
      case 'ConditionalExpression':
        node.test = await this.#handleFormulaNode(node.test, data, evaluate)
        node.consequent = await this.#handleFormulaNode(node.consequent, data, evaluate)
        node.alternate = await this.#handleFormulaNode(node.alternate, data, evaluate)
        break
      case 'CallExpression':
        if (node.callee.name === 'storeData') {
          node = {
            type: 'Literal',
            value: await this.#handleEvaluateNode(node, data),
            json: true
          }
        } else {
          node.arguments = await Promise.all(_.map(node.arguments, n => this.#handleFormulaNode(n, data, evaluate)))
        }

        break
      case 'ArrayExpression':
        node.elements = await Promise.all(_.map(node.elements, n => this.#handleFormulaNode(n, data, evaluate)))
        break
      case 'ObjectExpression':
        if (evaluate) {
          node = {
            type: 'Literal',
            value: await this.#handleEvaluateNode(node, data),
            json: true
          }
        } else {
          node.properties = await Promise.all(_.map(node.properties, n => this.#handleFormulaNode(n, data, evaluate)))
        }

        break
      case 'TemplateLiteral':
        if (evaluate) {
          node = {
            type: 'Literal',
            value: await this.#handleEvaluateNode(node, data)
          }
        } else {
          node.expressions = await Promise.all(_.map(node.expressions, n => this.#handleFormulaNode(n, data, evaluate)))
        }

        break
    }

    return node
  }

  async evaluateNode (node, data) {
    if (isLogop(node?.operator)) {
      node.type = 'LogicalExpression'
    }

    const formula = this.serializer.serializeNode(node)
    const result = await this.#handleEvaluateNode(node, data)

    if (this.history.shouldAdd(formula, result, node?.type)) {
      this.history.addEntry(
        await this.resolveFormula(this.serializer.serializeNode(node), data),
        result,
        node?.type
      )
    }

    return result
  }

  async #handleEvaluateNode (node, data) {
    switch (node?.type) {
      case 'Literal':
        return node.value
      case 'Vrn':
        return node.vrn
      case 'Annotation':
        return this.evaluateNode(node.expression, data)
      case 'Property':
        return {
          [node.key.name || node.key.value]: await this.evaluateNode(node.value, data)
        }
      case 'Identifier':
        return this.getValueOrAddUnresolved(node.name, data)
      case 'Task':
        return this.addUnresolved(
          _.chain([node.task, node.name]).compact().join('::').castArray().concat(this.serializer.serializeResult(await this.evaluateNode(node.taskArg, data), null, true)).compact().reject(v => _.includes(['null', 'undefined'], v)).join('(').castArray().concat(node.taskArg && ')').compact().join('').value(),
          data
        )
      case 'Compound':
        return Promise.all(_.map(node.body, n => this.evaluateNode(n, data)))
      case 'SequenceExpression':
        return Promise.all(_.map(node.expressions, n => this.evaluateNode(n, data)))
      case 'MemberExpression':
        return this.getValueOrAddUnresolved(this.getMemberKey(node), data)
      case 'UnaryExpression':
        return _.invoke(
          unops,
          node.operator,
          await this.evaluateNode(node.argument, data)
        )
      case 'LogicalExpression':
        return this.#evaluateLogical(node, data)
      case 'BinaryExpression':
        return _.invoke(binops, node.operator, ...[
          await this.evaluateNode(node.left, data),
          await this.evaluateNode(node.right, data)
        ])
      case 'ConditionalExpression':
        return this.#evaluateConditional(node, data)
      case 'CallExpression':
        return this.#evaluateCall(node, data)
      case 'ArrayExpression':
        return Promise.all(_.map(node.elements, n => this.evaluateNode(n, data)))
      case 'ObjectExpression':
        return _.merge(...(await Promise.all(_.map(node.properties, n => this.evaluateNode(n, data)))))
      case 'TemplateLiteral':
        return _.join(await Promise.all(_.chain(node.quasis).map((n, i) => n?.tail ? this.evaluateNode(n, data) : [this.evaluateNode(n, data), this.evaluateNode(node?.expressions[i], data)]).flattenDeep().value()), '')
      case 'TemplateElement':
        return node.value.cooked || node.value.raw
    }
  }

  async #evaluateLogical (node, data) {
    const left = await this.evaluateNode(node.left, data)

    if (node.operator === '||' && !left) {
      return this.evaluateNode(node.right, data)
    } else if (node.operator === '&&' && left) {
      return this.evaluateNode(node.right, data)
    }

    return left
  }

  async #evaluateConditional (node, data) {
    if (await this.evaluateNode(node.test, data)) {
      return await this.evaluateNode(node.consequent, data)
    }

    return this.evaluateNode(node.alternate, data)
  }

  async #evaluateCall (node, data) {
    if (node.callee.name) {
      const args = await Promise.all(_.map(node.arguments, n => this.evaluateNode(n, data)))

      if (node.callee.name === 'storeData') {
        return _.invoke(methods, node.callee.name, ...args, data)
      }

      return _.invoke(methods, node.callee.name, ...args)
    }

    const r = await this.evaluateNode(node.callee, data)

    if (_.isFunction(r)) {
      return r(...(await Promise.all(_.map(node.arguments, n => this.evaluateNode(n, data)))))
    }

    return r
  }
}
